import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { emailApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Mail, Search, AlertTriangle, Send, RotateCw, Cake, Award, Users, X } from 'lucide-react';

const TABS = [
  { key: 'compose', label: 'Compose' },
  { key: 'templates', label: 'Automated wishes' },
  { key: 'recipients', label: 'Recipients' },
  { key: 'exit-report', label: 'Exit report' },
  { key: 'log', label: 'Delivery log' },
];

function StatusPill({ status }) {
  const map = {
    sent: 'bg-[#E7F7EF] text-[#15654B]',
    failed: 'bg-[#FDE7E9] text-[#C2151F]',
    pending: 'bg-[#FBF0E6] text-[#B14A0E]',
  };
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize whitespace-nowrap ${map[status] || map.pending}`}>
      {status}
    </span>
  );
}

/* ------------------------------ Compose ------------------------------ */
function Compose({ tokens, onSent }) {
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(() => new Set());
  const [toAll, setToAll] = useState(false);
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    emailApi.recipients()
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error('Could not load employees'))
      .finally(() => setLoading(false));
  }, []);

  const withEmail = useMemo(() => rows.filter((r) => r.has_email), [rows]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.name || '').toLowerCase().includes(q)
      || (r.email || '').toLowerCase().includes(q)
      || (r.department || '').toLowerCase().includes(q));
  }, [rows, search]);

  const toggle = (id) => setPicked((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const recipientCount = toAll ? withEmail.length
    : rows.filter((r) => picked.has(r.id) && r.has_email).length;

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and message are both required'); return; }
    if (recipientCount === 0) { toast.error('Pick at least one recipient with an email address'); return; }
    if (!window.confirm(`Send this email to ${recipientCount} employee${recipientCount === 1 ? '' : 's'}?`)) return;
    setBusy(true);
    try {
      const r = await emailApi.send({
        to_all: toAll,
        employee_ids: toAll ? [] : [...picked],
        subject: subject.trim(),
        body: body.trim(),
      });
      const d = r.data;
      toast.success(`${d.attempted} queued — ${d.sent || 0} sent, ${d.failed || 0} failed, ${d.pending || 0} pending`);
      setSubject(''); setBody(''); setPicked(new Set()); setToAll(false);
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send'); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-5">
      <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
        <h2 className="text-sm font-semibold text-[#11182B] mb-3">Message</h2>
        <label className="block text-xs font-semibold text-[#5A6377] mb-1">Subject</label>
        <Input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
          placeholder="e.g. Plant shutdown notice — 15 August" className="mb-3" />
        <label className="block text-xs font-semibold text-[#5A6377] mb-1">Message</label>
        <textarea data-testid="email-body" value={body} onChange={(e) => setBody(e.target.value)} rows={12}
          placeholder="Type your message…"
          className="w-full border border-[#E6E9F0] rounded-md p-3 text-sm font-sans" />
        <p className="text-[11px] text-[#98A1B2] mt-2">
          Personalise with: {tokens.map((t) => (
            <button key={t} type="button" onClick={() => setBody((b) => b + t)}
              className="font-mono text-[#2563EB] hover:underline mr-1.5">{t}</button>
          ))}
        </p>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#F2F4F8]">
          <span className="text-xs text-[#5A6377]">
            {recipientCount} recipient{recipientCount === 1 ? '' : 's'}
          </span>
          <Button data-testid="email-send-btn" onClick={send} disabled={busy || recipientCount === 0}>
            <Send className="w-4 h-4 mr-1.5" /> {busy ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
        <h2 className="text-sm font-semibold text-[#11182B] mb-3">Recipients</h2>
        <label className="flex items-center gap-2 text-sm mb-3 cursor-pointer">
          <input data-testid="email-to-all" type="checkbox" checked={toAll}
            onChange={(e) => { setToAll(e.target.checked); if (e.target.checked) setPicked(new Set()); }} />
          <span className="font-medium text-[#11182B]">Everyone with an email</span>
          <span className="text-[11px] text-[#98A1B2]">({withEmail.length})</span>
        </label>

        {!toAll && (
          <>
            <div className="relative mb-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#98A1B2]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
                className="w-full border border-[#E6E9F0] rounded-md text-sm pl-9 pr-3 py-2" />
            </div>
            <div className="max-h-[420px] overflow-auto border border-[#F2F4F8] rounded-lg">
              {loading && <p className="text-xs text-[#98A1B2] p-3">Loading…</p>}
              {!loading && visible.length === 0 && <p className="text-xs text-[#98A1B2] p-3">No matches.</p>}
              {visible.map((r) => (
                <label key={r.id}
                  className={`flex items-start gap-2 px-3 py-2 border-b border-[#F7F8FB] last:border-0 ${r.has_email ? 'cursor-pointer hover:bg-[#F7F8FB]' : 'opacity-55'}`}>
                  <input type="checkbox" className="mt-1" disabled={!r.has_email}
                    checked={picked.has(r.id)} onChange={() => toggle(r.id)} />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-[#11182B] truncate">{r.name}</span>
                    <span className="block text-[11px] text-[#5A6377] truncate">
                      {r.has_email ? r.email : 'No email on record'}
                    </span>
                    <span className="block text-[10px] text-[#98A1B2] truncate">{r.department}</span>
                  </span>
                </label>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------------------- Recipients ----------------------------- */
/* Who is copied on each employee occasion. Everything here is ADDITIVE: the
   employee always receives their own wish, and the leadership and owner copies
   are set elsewhere and shown read-only so nobody edits this list believing it
   is the whole picture. */
const GROUP_ICON = { birthday: Cake, anniversary: Award, other: Users };

function Recipients({ canManage }) {
  const [groups, setGroups] = useState(null);
  const [also, setAlso] = useState(null);
  const [meta, setMeta] = useState({});
  const [draft, setDraft] = useState({});      // {groupKey: [address, ...]}
  const [adding, setAdding] = useState({});    // {groupKey: 'typed text'}
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    emailApi.occasionCc()
      .then((r) => {
        const g = r.data?.groups || [];
        setGroups(g);
        setAlso(r.data?.also_copied || null);
        setMeta({ updated_at: r.data?.updated_at, updated_by: r.data?.updated_by });
        setDraft(Object.fromEntries(g.map((x) => [x.key, [...(x.cc || [])]])));
      })
      .catch(() => toast.error('Could not load recipients'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saved = useMemo(
    () => Object.fromEntries((groups || []).map((g) => [g.key, g.cc || []])), [groups]);
  const dirty = useMemo(
    () => (groups || []).some((g) => (draft[g.key] || []).join(',') !== (saved[g.key] || []).join(',')),
    [groups, draft, saved]);

  const add = (key) => {
    const raw = (adding[key] || '').trim();
    if (!raw) return;
    // Accept a pasted "a@x, b@y" in one go — retyping each is the sort of
    // friction that makes people leave the list stale.
    const parts = raw.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    const bad = parts.filter((p) => !/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(p));
    if (bad.length) { toast.error(`Not a valid email address: ${bad.join(', ')}`); return; }
    setDraft((d) => {
      const have = new Set((d[key] || []).map((a) => a.toLowerCase()));
      const fresh = parts.filter((p) => !have.has(p.toLowerCase()));
      if (!fresh.length) { toast('Already on the list'); return d; }
      return { ...d, [key]: [...(d[key] || []), ...fresh] };
    });
    setAdding((a) => ({ ...a, [key]: '' }));
  };

  const remove = (key, addr) => setDraft((d) => ({
    ...d, [key]: (d[key] || []).filter((a) => a !== addr) }));

  const save = async () => {
    setSaving(true);
    try {
      await emailApi.saveOccasionCc(draft);
      toast.success('Recipients saved — the next email uses this list');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  if (!groups) return <div className="text-sm text-[#5A6377]">Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-[13px] text-[#5A6377] max-w-2xl">
          Extra people copied on employee occasion emails. The employee always receives
          their own message — adding someone here only sends them a copy, and never
          creates a second email.
        </p>
        {canManage && (
          <Button onClick={save} disabled={!dirty || saving} data-testid="occasion-cc-save">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        )}
      </div>

      {groups.map((g) => {
        const Icon = GROUP_ICON[g.key] || Mail;
        const list = draft[g.key] || [];
        return (
          <div key={g.key} data-testid={`occasion-cc-${g.key}`}
            className="bg-white border border-[#E6E9F0] rounded-xl p-5">
            <div className="flex items-center gap-2.5 mb-1">
              <Icon className="w-4 h-4 text-[#2563EB]" strokeWidth={1.75} />
              <h3 className="text-sm font-semibold text-[#11182B]">{g.label}</h3>
            </div>
            <p className="text-[12px] text-[#98A1B2] mb-3">Covers: {g.kinds.join(', ')}</p>

            <div className="flex flex-wrap gap-2 mb-3">
              {!list.length && (
                <span className="text-[12px] text-[#98A1B2]">
                  Nobody is copied on this occasion.
                </span>
              )}
              {list.map((addr) => (
                <span key={addr} data-testid={`occasion-cc-chip-${addr}`}
                  className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full
                             bg-[#F5F8FF] border border-[#D7E3FF] text-[12px] text-[#11182B]">
                  {addr}
                  {canManage && (
                    <button onClick={() => remove(g.key, addr)} title={`Remove ${addr}`}
                      data-testid={`occasion-cc-remove-${addr}`}
                      className="text-[#5A6377] hover:text-[#C2151F] rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>

            {canManage && (
              <div className="flex gap-2 max-w-md">
                <Input value={adding[g.key] || ''} placeholder="name@aampl.in"
                  data-testid={`occasion-cc-input-${g.key}`}
                  onChange={(e) => setAdding((a) => ({ ...a, [g.key]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(g.key); } }}
                  className="text-sm" />
                <Button variant="outline" onClick={() => add(g.key)}
                  data-testid={`occasion-cc-add-${g.key}`}>Add</Button>
              </div>
            )}
          </div>
        );
      })}

      {also && (
        <div className="bg-[#FAFBFD] border border-[#E6E9F0] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#11182B] mb-1">Also copied</h3>
          <p className="text-[12px] text-[#98A1B2] mb-3">
            Set outside this screen and listed here so the full picture is visible.
          </p>
          <div className="text-[12.5px] text-[#5A6377] space-y-1">
            <div><b className="text-[#11182B]">Leadership</b> (birthday, anniversary, welcome):{' '}
              {(also.leadership || []).join(', ') || '—'}</div>
            <div><b className="text-[#11182B]">Owner</b> (all lifecycle events): {also.owner || '—'}</div>
          </div>
        </div>
      )}

      {meta.updated_at && (
        <p className="text-[11px] text-[#98A1B2]">
          Last changed {formatDateTime(meta.updated_at)}{meta.updated_by ? ` by ${meta.updated_by}` : ''}.
        </p>
      )}
      {!canManage && (
        <p className="text-[12px] text-[#98A1B2]">
          You can view this list. Changing it needs the email management permission.
        </p>
      )}
    </div>
  );
}

/* ----------------------------- Templates ----------------------------- */
function Templates({ tokens }) {
  const [tpls, setTpls] = useState([]);
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    emailApi.templates()
      .then((r) => setTpls(r.data?.templates || []))
      .catch(() => toast.error('Could not load templates'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (key, field, val) => setTpls((s) =>
    s.map((t) => (t.key === key ? { ...t, [field]: val } : t)));

  const save = async (t) => {
    setBusy(t.key);
    try {
      await emailApi.saveTemplate(t.key, { subject: t.subject, body: t.body, active: t.active });
      toast.success('Template saved');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setBusy(''); }
  };

  const meta = {
    birthday: { icon: Cake, title: 'Birthday wish', when: 'Sent automatically on each employee\'s date of birth.' },
    anniversary: { icon: Award, title: 'Work anniversary wish', when: 'Sent automatically each year on their joining date.' },
  };

  return (
    <div className="space-y-5">
      {tpls.map((t) => {
        const m = meta[t.key] || { icon: Mail, title: t.key, when: '' };
        const Icon = m.icon;
        return (
          <div key={t.key} data-testid={`email-template-${t.key}`} className="bg-white border border-[#E6E9F0] rounded-xl p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-[#E8EFFD] text-[#15307F] flex items-center justify-center">
                  <Icon className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-[#11182B]">{m.title}</h3>
                  <p className="text-[11px] text-[#98A1B2]">{m.when}</p>
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-[#5A6377] whitespace-nowrap cursor-pointer">
                <input type="checkbox" checked={t.active}
                  onChange={(e) => setField(t.key, 'active', e.target.checked)} />
                Enabled
              </label>
            </div>

            <label className="block text-xs font-semibold text-[#5A6377] mb-1">Subject</label>
            <Input value={t.subject} onChange={(e) => setField(t.key, 'subject', e.target.value)} className="mb-3" />
            <label className="block text-xs font-semibold text-[#5A6377] mb-1">Message</label>
            <textarea value={t.body} onChange={(e) => setField(t.key, 'body', e.target.value)} rows={9}
              className="w-full border border-[#E6E9F0] rounded-md p-3 text-sm font-sans" />
            <p className="text-[11px] text-[#98A1B2] mt-2">
              Available: {tokens.map((x) => <span key={x} className="font-mono mr-1.5">{x}</span>)}
            </p>
            <div className="flex justify-end mt-3">
              <Button size="sm" onClick={() => save(t)} disabled={busy === t.key}>
                {busy === t.key ? 'Saving…' : 'Save template'}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* -------------------------------- Log -------------------------------- */
function DeliveryLog({ refreshKey }) {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    emailApi.log({ kind: kind || undefined, status: status || undefined })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load the log'))
      .finally(() => setLoading(false));
  }, [kind, status]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const c = data?.counts || {};
  return (
    <div className="bg-white border border-[#E6E9F0] rounded-xl overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 p-4 border-b border-[#F2F4F8]">
        <span className="text-xs text-[#5A6377] mr-2">
          <b className="text-[#15654B]">{c.sent || 0}</b> sent ·{' '}
          <b className="text-[#C2151F]">{c.failed || 0}</b> failed ·{' '}
          <b className="text-[#B14A0E]">{c.pending || 0}</b> pending
        </span>
        <select value={kind} onChange={(e) => setKind(e.target.value)}
          className="border border-[#E6E9F0] rounded-md text-sm px-2 py-1.5 ml-auto">
          <option value="">All types</option>
          <option value="birthday">Birthday</option>
          <option value="anniversary">Work anniversary</option>
          <option value="manual">Manual</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="border border-[#E6E9F0] rounded-md text-sm px-2 py-1.5">
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
        </select>
        <Button size="sm" variant="outline" onClick={load}><RotateCw className="w-3.5 h-3.5" /></Button>
      </div>

      {loading ? <p className="text-sm text-[#5A6377] p-6">Loading…</p>
        : (data?.rows || []).length === 0 ? <p className="text-sm text-[#98A1B2] p-8 text-center">No emails yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="bg-[#F7F8FB] text-[11px] uppercase tracking-wider text-[#5A6377]">
                  <th className="text-left font-semibold px-4 py-2.5">Recipient</th>
                  <th className="text-left font-semibold px-4 py-2.5">Subject</th>
                  <th className="text-left font-semibold px-4 py-2.5">Type</th>
                  <th className="text-left font-semibold px-4 py-2.5 whitespace-nowrap">Date / time</th>
                  <th className="text-left font-semibold px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} data-testid={`email-log-${r.id}`} className="border-t border-[#F2F4F8] align-top">
                    <td className="px-4 py-2.5">
                      <div className="text-[#11182B]">{r.to_name || '—'}</div>
                      <div className="text-[11px] text-[#98A1B2]">{r.to_email}</div>
                    </td>
                    <td className="px-4 py-2.5 text-[#5A6377]">{r.subject}</td>
                    <td className="px-4 py-2.5 text-[11px] text-[#5A6377] capitalize whitespace-nowrap">
                      {r.kind}
                      {r.provider && <div className="text-[10px] text-[#98A1B2] capitalize">{r.provider}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-[11px] text-[#5A6377] whitespace-nowrap tabular-nums">
                      {formatDateTime(r.sent_at || r.created_at)}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusPill status={r.status} />
                      {r.error && <div className="text-[10px] text-[#C2151F] mt-1 max-w-[240px]">{r.error}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

/* --------------------------- Monthly exit report --------------------------- */
/** Preview what the month's exit report says before anyone's inbox sees it, then
 *  send it for real or to a single test address. The real send is idempotent —
 *  the server skips a month it has already reported. */
function ExitReport({ canManage, onSent }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback((m) => {
    setLoading(true);
    emailApi.exitReportPreview(m)
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); toast.error(e?.response?.data?.detail || 'Could not load the report'); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(month); }, [month, load]);

  const sendTest = async () => {
    const to = window.prompt('Send a test copy of this report to which address?', '');
    if (!to) return;
    setBusy(true);
    try {
      const r = await emailApi.sendExitReport(month, to.trim());
      if (r.data.status === 'sent') toast.success(`Test copy sent to ${r.data.sent_to}.`);
      else if (r.data.status === 'pending') toast.warning('No provider configured — logged as Pending.');
      else toast.error(`Failed: ${r.data.error || 'unknown error'}`);
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send the test'); }
    finally { setBusy(false); }
  };

  const sendReal = async () => {
    if (!window.confirm(
      `Send the ${data?.month_label || month} exit report to leadership and each plant head now?\n\n`
      + 'A month that has already been reported is skipped, so this cannot duplicate.')) return;
    setBusy(true);
    try {
      const r = await emailApi.sendExitReport(month);
      const d = r.data;
      if (d.sent) toast.success(`Sent ${d.sent} report${d.sent === 1 ? '' : 's'}.`
        + (d.skipped_already_sent ? ` ${d.skipped_already_sent} already sent.` : ''));
      else if (d.skipped_already_sent) toast.info('Already sent for this month — nothing to do.');
      else if (d.failed) toast.error(`${d.failed} report(s) failed. Check the delivery log.`);
      else toast.info('Nothing was sent.');
      load(month);
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send the report'); }
    finally { setBusy(false); }
  };

  const cols = ['Employee Name', 'Employee ID', 'Department', 'Designation', 'Plant',
    'Date of Joining', 'Date of Leaving', 'Reason for Leaving'];
  const keys = ['name', 'code', 'department', 'designation', 'unit', 'doj', 'dol', 'reason'];
  const orphanPlants = (data?.plants || []).filter((p) => p.plant_head_missing);

  return (
    <div data-testid="exit-report-tab" className="surface rounded-[14px] p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="text-sm font-semibold text-[#11182B] mb-1">Monthly employee exit report</h2>
          <p className="text-xs text-[#5A6377] max-w-[640px]">
            Sent automatically at month end. Leadership receives one consolidated report covering
            every plant; each plant head receives only their own plant. Preview here first — this
            view sends nothing.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label htmlFor="exit-month" className="block text-[11px] text-[#5A6377] mb-1">Month</label>
            <Input id="exit-month" data-testid="exit-report-month" type="month" value={month}
              onChange={(e) => setMonth(e.target.value)} className="w-[160px]" />
          </div>
          {canManage && (
            <>
              <Button variant="outline" onClick={sendTest} disabled={busy || loading}
                data-testid="exit-report-test">
                <Send className="w-4 h-4 mr-1.5" /> Test send
              </Button>
              <Button variant="outline" onClick={sendReal} disabled={busy || loading}
                data-testid="exit-report-send">
                <RotateCw className="w-4 h-4 mr-1.5" /> {busy ? 'Sending…' : 'Send report now'}
              </Button>
            </>
          )}
        </div>
      </div>

      {loading && <div className="text-sm text-[#5A6377]">Loading…</div>}

      {!loading && data && (
        <>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 text-xs">
            <span className="text-[#11182B] font-semibold" data-testid="exit-report-total">
              Total Employees Left This Month: {data.total}
            </span>
            <span className="text-[#5A6377]">Subject: <span className="text-[#11182B]">{data.subject}</span></span>
            <span className="text-[#5A6377]">
              Consolidated to: <span className="text-[#11182B]">{data.consolidated_to.join(', ')}</span>
            </span>
            {data.already_sent?.consolidated && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#E7F7EF] text-[#15654B]">
                Already sent for {data.month_label}
              </span>
            )}
          </div>

          {orphanPlants.length > 0 && (
            <div data-testid="exit-report-no-head"
              className="mb-4 flex items-start gap-2 text-[12px] text-[#B14A0E] bg-[#FFF7E6] border border-[#FDD08A] rounded-md p-3">
              <AlertTriangle className="w-4 h-4 mt-[1px] flex-shrink-0" />
              <div>
                <b>No plant head email on record for {orphanPlants.map((p) => p.unit).join(', ')}.</b>{' '}
                Those {orphanPlants.reduce((s, p) => s + p.count, 0)} leaver(s) still appear in the
                consolidated report, but no plant-specific mail will go out. Add an email to the
                plant head&rsquo;s employee record to fix this.
              </div>
            </div>
          )}

          {data.total === 0 ? (
            <div data-testid="exit-report-empty"
              className="rounded-md border border-[#E6E9F0] bg-[#F7F9FC] p-6 text-center text-sm text-[#11182B]">
              No employees left the company during {data.month_label}.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-[#E6E9F0]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F7F9FC] text-left">
                    {cols.map((c) => (
                      <th key={c} className="px-3 py-2 text-[11px] font-semibold text-[#5A6377] whitespace-nowrap">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={`${r.code}-${i}`} data-testid="exit-report-row"
                      className="border-t border-[#E6E9F0]">
                      {keys.map((k) => (
                        <td key={k} className={`px-3 py-2 text-[12px] ${
                          k === 'name' ? 'font-semibold text-[#11182B]' : 'text-[#11182B]'} ${
                          ['doj', 'dol', 'code'].includes(k) ? 'whitespace-nowrap' : ''}`}>
                          {k === 'name' ? (
                            <span className="flex items-center gap-2">
                              {r.photo
                                ? <img src={r.photo} alt="" className="w-8 h-8 rounded-full object-cover
                                                                       border border-[#E6E9F0] flex-shrink-0" />
                                : <span aria-hidden="true"
                                    className="w-8 h-8 rounded-full flex-shrink-0 grid place-items-center
                                               text-[11px] font-bold text-white bg-[#16295E]">
                                    {r.initial}
                                  </span>}
                              <span className="whitespace-nowrap">{r.name}</span>
                            </span>
                          ) : r[k]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.plants.length > 0 && (
            <div className="mt-5">
              <h3 className="text-xs font-semibold text-[#11182B] mb-2">Plant-head copies</h3>
              <div className="space-y-1.5">
                {data.plants.map((p) => (
                  <div key={p.unit} className="flex items-center justify-between gap-3 text-xs
                                               border border-[#E6E9F0] rounded-md px-3 py-2">
                    <span className="text-[#11182B] font-medium">{p.unit}</span>
                    <span className="text-[#5A6377]">
                      {p.count} leaver{p.count === 1 ? '' : 's'} &middot;{' '}
                      {p.plant_head_missing
                        ? <span className="text-[#B14A0E] font-semibold">no plant head email</span>
                        : p.plant_head_email}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="mt-5 text-xs text-[#5A6377]">Regards,<br /><b className="text-[#11182B]">HR Department</b></p>
        </>
      )}
    </div>
  );
}

/* ------------------------------- Page -------------------------------- */
export default function EmailCenter() {
  const { hasPerm } = useAuth();
  const [tab, setTab] = useState('compose');
  const [status, setStatus] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [running, setRunning] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadStatus = useCallback(() => {
    emailApi.status().then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const sendTest = async () => {
    const to = window.prompt('Send a test email to which address?', '');
    if (!to) return;
    setTesting(true);
    try {
      const r = await emailApi.test(to.trim());
      const d = r.data;
      if (d.status === 'sent') toast.success(`Test sent via ${d.provider}. Check the inbox.`);
      else if (d.status === 'pending') toast.warning('No provider configured — logged as Pending.');
      else toast.error(`Failed: ${d.error || 'unknown error'}`);
      setRefreshKey((k) => k + 1);
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send test'); }
    finally { setTesting(false); }
  };

  const runNow = async () => {
    if (!window.confirm('Send today\'s birthday and anniversary wishes now? Anyone already wished today is skipped.')) return;
    setRunning(true);
    try {
      const r = await emailApi.runAutomation();
      const s = r.data?.sent || {};
      toast.success(`Birthday: ${s.birthday || 0}, Anniversary: ${s.anniversary || 0} · ${r.data?.already_sent || 0} already done`);
      setRefreshKey((k) => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not run'); }
    finally { setRunning(false); }
  };

  const tokens = status?.tokens || [];
  const canManage = hasPerm('email.manage');
  const canSend = hasPerm('email.send');
  const visibleTabs = TABS.filter((t) => (t.key === 'compose' ? canSend : t.key === 'templates' ? canManage : true));

  return (
    <div data-testid="email-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                  style={{ background: 'linear-gradient(135deg,#15307F,#2563EB)' }}>
                  <Mail className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold tracking-tight text-[#11182B]">Email</h1>
                  <p className="text-sm text-[#5A6377]">
                    Birthday and work-anniversary wishes go out automatically
                    {status ? ` at ${String(status.send_hour).padStart(2, '0')}:00 daily` : ''}
                    {status?.provider ? ` via ${status.provider === 'brevo' ? 'Brevo' : 'SMTP'}` : ''}.
                    {status ? ` ${status.employees_with_email} of ${status.employees_total} employees have an email on record.` : ''}
                  </p>
                </div>
              </div>
              {canManage && (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={sendTest} disabled={testing}>
                    <Send className="w-4 h-4 mr-1.5" /> {testing ? 'Sending…' : 'Send test'}
                  </Button>
                  <Button variant="outline" onClick={runNow} disabled={running}>
                    <RotateCw className="w-4 h-4 mr-1.5" /> {running ? 'Running…' : 'Run today\'s wishes now'}
                  </Button>
                </div>
              )}
            </div>

            {status && !status.configured && (
              <div data-testid="smtp-warning" className="mb-5 flex items-start gap-2 text-[12px] text-[#B14A0E] bg-[#FFF7E6] border border-[#FDD08A] rounded-md p-3">
                <AlertTriangle className="w-4 h-4 mt-[1px] flex-shrink-0" />
                <div><b>Email sending is not switched on yet.</b> {status.note}</div>
              </div>
            )}

            <div className="flex gap-1 mb-5 border-b border-[#E6E9F0]">
              {visibleTabs.map((t) => (
                <button key={t.key} data-testid={`email-tab-${t.key}`} onClick={() => setTab(t.key)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    tab === t.key ? 'border-[#2563EB] text-[#11182B]' : 'border-transparent text-[#5A6377] hover:text-[#11182B]'}`}>
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'compose' && canSend && <Compose tokens={tokens} onSent={() => setRefreshKey((k) => k + 1)} />}
            {tab === 'templates' && canManage && <Templates tokens={tokens} />}
            {tab === 'recipients' && <Recipients canManage={canManage} />}
            {tab === 'exit-report' && <ExitReport canManage={canManage} onSent={() => setRefreshKey((k) => k + 1)} />}
            {tab === 'log' && <DeliveryLog refreshKey={refreshKey} />}
          </div>
        </main>
      </div>
    </div>
  );
}
